import BenefitsWizardContainer from 'containers/Enrollment/BenefitsWizard'
import DocumentsWizard from 'containers/Enrollment/DocumentsWizard'
import HouseholdIncomeWizardContainer from 'containers/Enrollment/HouseholdIncome'
import ReviewWizardContainer from 'containers/Enrollment/ReviewWizard'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import SPage, { SPageType } from '../../../components/opinionless/SPage'
import { SStyled, useSTheme } from '../../../styles/theme'
import { ApplicationContext } from '../../../v2/context/application'
import XProgressBar from 'v2/components/XProgressBar'
import { useTranslation } from 'i18n'
import FNeedHelpCTA from 'components/BaseComponents/FNeedHelpCTA'
import { Container } from '../steps/ApplicationStep1'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import ApplicationStepLBIntroduction from '../steps/ApplicationStepLBIntroduction'
import ApplicationStepLBRemainingQuestions from '../steps/ApplicationStepLBRemainingQuestions'
import ApplicationStepLBBasicInfo from '../steps/ApplicationStepLBasicInfo'
import ConditionalRender from '../../../utils/conditionalRender'

interface Props {
    applicationId?: string
    category?: string
}

export const LBPage = SStyled(SPage)`
    margin: 10px 80px;
    padding: 0px;

   ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin: 24px 24px;
            padding: 0px;
        }
    `}
`

export const WIZARD_STEPS_MAPPING = {
    basicInfo: { introduction: 1, applicantInfo: 2, applicantRemainingInfo: 3 },
    householdInfo: {
        introduction: 1,
        members: 2,
        dob: 3,
        blindDisabledChoice: 4,
        blindDisabled: 5,
        incomeChoice: 6,
        incomeValue: 7,
        incomeEligibility: 8,
        review: 9,
    },
    benefits: { introduction: 1, selection: 2, ssi: 3, hud: 4, calFresh: 5, calWorks: 6, headStart: 7, acp: 8 },
    documents: {
        introduction: 1,
        proofOfAddressChoice: 2,
        proofOfAddressUpload: 3,
        householdIncomeUploadOne: 4,
        householdIncomeUploadTwo: 5,
    },
    review: { introduction: 1, electronicSignature: 2, applicationComplete: 3 },
}

const CATEGORY_FIRST_STEP_MAPPING = {
    basic: 2,
    household: 4,
    default: 1,
    benefits: 5,
    documents: 6,
    review: 7,
}

const Index: React.FC<Props> = (props: Props) => {
    const [successMessage, setSuccessMessage] = useState('')
    const [fromBackButton, setFromBackButton] = useState(false)
    const [currentStep, setCurrentStep] = useState(
        CATEGORY_FIRST_STEP_MAPPING[props.category] || CATEGORY_FIRST_STEP_MAPPING.default
    )

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const longBeachFontFamily = theme.fonts.longBeach
    const applicationContext = useContext(ApplicationContext)

    const { called: calledApplicationBasicInfo, refetch: refetchApplicationBasicInfo } = useApplicationBasicInfo()
    const { called: calledApplicationAnswers, getAnswersByCategory } = useApplicationAnswers()
    const { called: calledHouseholdMembers } = useHouseholdMembers()

    useEffect(() => {
        refetchApplicationBasicInfo()
    }, [refetchApplicationBasicInfo, applicationContext, applicationContext.application.completed])

    useEffect(() => {
        if (props.applicationId && calledApplicationAnswers) {
            const sign_certification_answer = getAnswersByCategory('SIGN_VERIFICATION')?.[0]
            if (sign_certification_answer) {
                return setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['review'])
            }
        }
    }, [calledApplicationAnswers])

    useEffect(() => {
        if (props.applicationId && calledApplicationAnswers) {
            const sign_certification_answer = getAnswersByCategory('SIGN_VERIFICATION')?.[0]
            if (sign_certification_answer && !window.location.search) {
                return setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['review'])
            }
        }

        if (
            !props.category &&
            props.applicationId &&
            calledApplicationAnswers &&
            calledHouseholdMembers &&
            calledApplicationBasicInfo
        ) {
            if (applicationContext.documents.length) {
                return setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['documents'])
            }

            if (applicationContext.benefits.length) {
                setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['benefits'])
            }

            if (applicationContext.householdMembers.length) {
                return setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['household'])
            }

            return setCurrentStep(CATEGORY_FIRST_STEP_MAPPING['basic'])
        }
    }, [applicationContext])

    useEffect(() => {
        if (successMessage) {
            setTimeout(() => setSuccessMessage(''), 3000)
        }
    }, [successMessage])

    const [percentage, setPercentage] = useState(0)

    const updatePercentage = (calculatedPercentage, wizardStep, currentStep) => {
        const householdIndex = Object.keys(WIZARD_STEPS_MAPPING).indexOf(wizardStep)
        const percentage = parseInt(calculatedPercentage) + (wizardStep === 'basicInfo' ? 0 : householdIndex * 100)
        setPercentage(percentage)
        window.location.hash = `step=${currentStep || 0}`
    }

    useEffect(() => {
        if (window.location.search && currentStep !== 6) {
            setCurrentStep(6)
        }
    }, [window.location.search])

    const incomeDocumentsRequired = useMemo(() => {
        const requiredIncomeDocumentsApplicant =
            parseInt(applicationContext.profile.earnedIncome, 10) ||
            parseInt(applicationContext.profile.unearnedIncome, 10)
                ? 1
                : 0
        let requiredIncomeDocumentsForMembers = 0

        applicationContext.householdMembers.forEach((householdMember) => {
            if (parseInt(householdMember.earnedIncome, 10) || parseInt(householdMember.unearnedIncome, 10)) {
                requiredIncomeDocumentsForMembers += 1
            }
        })

        return requiredIncomeDocumentsApplicant || requiredIncomeDocumentsForMembers
    }, [applicationContext.profile, applicationContext.householdMembers])

    const progressBarSegments = 4

    return (
        <LBPage
            pageType={SPageType.Unauthed}
            display={'flex'}
            sx={{
                alignItems: 'center',
                justifyContent: ['row', 'flex-start ', 'flex-start'],
                flexDirection: 'column',
                position: 'relative',
                mb: '20px',
            }}
        >
            <ConditionalRender if={currentStep > WIZARD_STEPS_MAPPING.basicInfo.introduction}>
                <XProgressBar segments={progressBarSegments} progress={percentage} />
            </ConditionalRender>

            <>
                {currentStep === 1 && (
                    <ApplicationStepLBIntroduction
                        setCurrentStep={setCurrentStep}
                        fontFamily={longBeachFontFamily}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 2 && (
                    <ApplicationStepLBBasicInfo
                        setCurrentStep={setCurrentStep}
                        previousStep={() => {
                            setFromBackButton(true)
                            setCurrentStep(1)
                        }}
                        onSuccess={() => {
                            setFromBackButton(false)
                            setCurrentStep(3)
                        }}
                        fontFamily={longBeachFontFamily}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 3 && (
                    <ApplicationStepLBRemainingQuestions
                        previousStep={() => {
                            setFromBackButton(true)
                            setCurrentStep(2)
                        }}
                        onSuccess={() => {
                            setFromBackButton(false)
                            setCurrentStep(4)
                        }}
                        setCurrentStep={setCurrentStep}
                        fontFamily={longBeachFontFamily}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 4 && (
                    <HouseholdIncomeWizardContainer
                        previousStep={() => {
                            setFromBackButton(true)
                            setCurrentStep(3)
                        }}
                        onSuccess={() => {
                            setFromBackButton(false)
                            setCurrentStep(5)
                        }}
                        setCurrentStep={setCurrentStep}
                        fontFamily={longBeachFontFamily}
                        fromBackButton={fromBackButton}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 5 && (
                    <BenefitsWizardContainer
                        previousStep={() => {
                            setFromBackButton(true)
                            setCurrentStep(4)
                        }}
                        step={currentStep}
                        goToNextStep={() => {
                            setFromBackButton(false)
                            setCurrentStep(6)
                        }}
                        fromBackButton={fromBackButton}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 6 && (
                    <DocumentsWizard
                        onPrevious={() => {
                            setFromBackButton(true)
                            setCurrentStep(5)
                        }}
                        onSuccess={() => {
                            setFromBackButton(false)
                            setCurrentStep(7)
                        }}
                        fromBackButton={fromBackButton}
                        requestNewDocumentCategory={window.location.search}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            <>
                {currentStep === 7 && (
                    <ReviewWizardContainer
                        previousStepForWizard={() => {
                            if (incomeDocumentsRequired) {
                                setFromBackButton(true)
                            }
                            setCurrentStep(6)
                        }}
                        setCurrentStep={setCurrentStep}
                        fontFamily={longBeachFontFamily}
                        requestNewDocumentFlow={!!window.location.search}
                        updatePercentage={updatePercentage}
                    />
                )}
            </>
            {!applicationContext.application.completed && (
                <Container>
                    <FNeedHelpCTA
                        url={`${
                            window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                        }/help`}
                        text={t('need_help_question')}
                        marginTop="0px"
                    />
                </Container>
            )}
        </LBPage>
    )
}

export default Index
